import React from "react";
import { Box, Dialog } from "@mui/material";
import DrawerHeader from "../atoms/DrawerHeader";
import { COLORS } from "../../styles/uiConstants";
import DrawerFooter from "../atoms/DrawerFooter";

/**
 * Form dialog kabuğu: `DrawerHeader` (açık gradient başlık) + kaydırılabilir
 * gövde + `DrawerFooter` (İptal + ActionButton). Şablon sayfaları, sistem
 * değişkeni, cihaz durumu gibi dialog formlarında her sayfanın kendi
 * Dialog + PaperProps + DrawerHeader bloğunu kopyalamasının yerine geçer.
 *
 * `accent` (opsiyonel) başlığın vurgu tonudur; verilmezse ve `submitColor="error"`
 * ise kırmızı (silme/iade gibi yıkıcı formlar), değilse tema rengi.
 *
 * Yükleme sırasında (`disableCloseWhileLoading`) backdrop / Escape / kapat
 * butonu dialog'u kapatmaz ve gövde tıklamaya kapanır.
 *
 * @param {boolean} open
 * @param {() => void} onClose - İptal / kapat / backdrop / Escape
 * @param {React.ReactNode} [icon] - Başlık ikon çipi
 * @param {React.ReactNode} title
 * @param {React.ReactNode} [subtitle]
 * @param {"xs"|"sm"|"md"|"lg"} [maxWidth="sm"]
 * @param {boolean} [loading=false] - Kaydediliyor durumu (submit butonunda spinner)
 * @param {string} [submitLabel="Kaydet"]
 * @param {string} [loadingLabel] - loading iken submit metni
 * @param {() => void} [onSubmit] - Yoksa footer'da sadece İptal çizilir
 * @param {boolean} [submitDisabled=false]
 * @param {"primary"|"error"} [submitColor="primary"]
 * @param {React.ReactNode} [submitIcon] - Submit buton ikonu (ActionButton varsayılanı yerine)
 * @param {string} [cancelLabel="İptal"]
 * @param {boolean} [disableCloseWhileLoading=true]
 * @param {React.ReactNode|null} [footer] - Verilirse DrawerFooter yerine çizilir
 *   (çok adımlı sihirbazlar vb.); `null` footer'ı tamamen gizler
 * @param {object} [bodySx] - Gövde sx ek/override
 * @param {React.ReactNode} children - Form gövdesi
 */
export default function FormDialog({
  accent,
  open,
  onClose,
  icon,
  title,
  subtitle,
  maxWidth = "sm",
  loading = false,
  submitLabel = "Kaydet",
  loadingLabel,
  onSubmit,
  submitDisabled = false,
  submitColor = "primary",
  submitIcon,
  cancelLabel = "İptal",
  disableCloseWhileLoading = true,
  footer,
  bodySx = {},
  children,
}) {
  const locked = loading && disableCloseWhileLoading;
  const handleClose = () => {
    if (locked) return;
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={locked}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
      }}
    >
      <DrawerHeader
        icon={icon}
        title={title}
        subtitle={subtitle}
        onClose={handleClose}
        bgcolor={accent || (submitColor === "error" ? COLORS.error : undefined)}
      />
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          pointerEvents: locked ? "none" : "auto",
          ...bodySx,
        }}
      >
        {children}
      </Box>
      {footer !== undefined ? (
        footer
      ) : (
        <DrawerFooter
          onCancel={handleClose}
          onSubmit={onSubmit}
          cancelLabel={cancelLabel}
          submitLabel={submitLabel}
          loadingLabel={loadingLabel}
          loading={loading}
          disabled={submitDisabled}
          submitColor={submitColor}
          startIcon={submitIcon}
        />
      )}
    </Dialog>
  );
}
